import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { map } from 'lodash';

import { Row, Col, Card, Form, Alert } from 'react-bootstrap-5';
import ReCAPTCHA from 'react-google-recaptcha-enterprise';

import { Button } from 'components/ui/button';

import axios from 'axios';
import { get } from 'helpers/api';
import { getQueryParameter } from 'helpers/browser_helpers';
import PhoneNumberInput from 'components/ui/phone_number_input';
import snakecaseKeys from 'snakecase-keys';
import { countries, isEuCountry, getUnitedStatesId } from 'helpers/countries';
import { getSourceParam } from '../helpers/vault_access_helper';
import { setShowInfoRequest } from '../actions/ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faMessage } from '@fortawesome/pro-solid-svg-icons';
import MarketingPreferences from './marketing_preferences';
import { fetchTranslation } from '../../../helpers/i18n';
import LoadingSpinner from 'components/ui/loading_spinner';

export const AXIOS_REQUEST_HEADERS = {
  headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
};

export function RequestMoreInfo({ accountPath, propertyId, recaptchaKey, setShowInfoRequest, sharingId }) {
  const [email, setEmail] = React.useState('');
  const [name, setName] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [countryId, setCountryId] = React.useState(getUnitedStatesId());
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [loggedIn, setLoggedIn] = React.useState(false);
  const [errors, setErrors] = React.useState([]);
  const [marketingPreferences, setMarketingPreferences] = React.useState([]);
  const [formSubmitted, setFormSubmitted] = React.useState(false);

  const recaptchaRef = React.useRef();
  const fastCaToken = getQueryParameter('fast_ca');
  const isEuCountrySelected = isEuCountry(countryId);
  const isUsSelected = countryId === getUnitedStatesId();

  React.useEffect(() => {
    get(accountPath).then((response) => {
      if (response.profile) setLoggedIn(true);
      setLoading(false);
    }).catch(() => setLoading(false)); // ignore for missing profile
  }, []);

  const submitForm = async () => {
    setFormSubmitted(true);

    if (!loggedIn && isEuCountrySelected && marketingPreferences.length === 0) {
      return;
    }

    setSaving(true);
    const source = getSourceParam();

    let recaptchaToken = '';
    try {
      recaptchaToken = await recaptchaRef.current.executeAsync();
      recaptchaRef.current.reset();
    } catch (e) {
      setSaving(false);
      return;
    }

    axios.post(
      '/plugin_more_info_requests',
      {
        'g-recaptcha-response': recaptchaToken,
        enterprise_recaptcha: true,
        plugin_more_info_request: snakecaseKeys({
          fastCa: fastCaToken,
          propertyId,
          sharingId,
          message,
          email,
          phone,
          name,
          countryId,
          marketingPreferences,
          ...(source && { source })
        })
      },
      AXIOS_REQUEST_HEADERS
    ).then((response) => {
      if (response.data.errors) {
        setSaving(false);
        setErrors(response.data.errors);
      } else {
        setSaved(true);
        setTimeout(() => setShowInfoRequest(false), 2500);
      }
    });
  };

  const disableSave = () => {
    if (loggedIn) return saving || saved;

    const formDataDisabled = (!loggedIn && (!name || !email || !phone));

    return saving || saved || formDataDisabled;
  };

  return (
    <React.Fragment>
      <Row className="justify-content-center py-6 gx-0 my-auto">
        <Col className="connect-card-container" lg={10} xl={8} xxl={7}>
          <Card className="overflow-hidden connect-card">
            <Card.Body className="p-5">
              <button className="btn btn-link mb-2" onClick={() => setShowInfoRequest(false)}>
                <FontAwesomeIcon icon={faChevronLeft}/>
                {fetchTranslation('back')}
              </button>
              {loading ? (
                <div className="text-center text-muted">
                  <LoadingSpinner name="loading-spinner" />
                </div>
              ) : (
                <React.Fragment>
                  <h5 className="text-center mb-4 strong">
                    {saved ? fetchTranslation('request_sent') : (
                      <React.Fragment>
                        <FontAwesomeIcon className="me-2" icon={faMessage}/>
                        {fetchTranslation('request_more_information')}
                      </React.Fragment>
                    )}
                  </h5>
                  <p className="text-center">
                    {fetchTranslation('interested_in_property')}
                  </p>
                  {errors.length > 0 && (
                    <Alert variant="danger">
                      {uniq(errors).map(error => (
                        <div key={error}>{error}</div>
                      ))}
                    </Alert>
                  )}
                  {!loggedIn && (
                    <React.Fragment>
                      <Form.Group className="mb-4">
                        <Form.Label>
                          {fetchTranslation('name')} <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          autoFocus
                          name="name"
                          placeholder="John Smith"
                          required
                          value={name}
                          onChange={e => setName(e.target.value)}
                        />
                      </Form.Group>
                      <Form.Group className="mb-4">
                        <Form.Label>{fetchTranslation('email_address')}
                          <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          name="email"
                          placeholder={fetchTranslation('enter_email_address')}
                          required
                          value={email}
                          onChange={e => setEmail(e.target.value)}
                        />
                      </Form.Group>
                      <Form.Group className="mb-4">
                        <Form.Label>{fetchTranslation('phone_number')}
                          <span className="text-danger">*</span>
                        </Form.Label>
                        <PhoneNumberInput
                          defaultCountry="US"
                          name="phone"
                          placeholder={fetchTranslation('phone_number_placeholder')}
                          useNationalFormatForDefaultCountryValue={false}
                          value={phone}
                          onChange={setPhone}
                        />
                      </Form.Group>
                      <Form.Group className="mb-4">
                        <Form.Label>
                          {fetchTranslation('country')} <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                          name="country"
                          value={countryId}
                          onChange={e => setCountryId(Number(e.target.value))}
                        >
                          {map(countries, ({ id, label }) => (
                            <option key={id} value={id}>{label}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                      {!isUsSelected && (
                        <MarketingPreferences
                          required={isEuCountrySelected}
                          showError={formSubmitted}
                          value={marketingPreferences}
                          onChange={setMarketingPreferences}
                        />
                      )}
                    </React.Fragment>
                  )}
                  <Form.Group className="mb-4">
                    <Form.Label>{fetchTranslation('message')}</Form.Label>
                    <Form.Control
                      as="textarea"
                      name="message"
                      placeholder={fetchTranslation('message_placeholder')}
                      required
                      value={message}
                      onChange={e => setMessage(e.target.value)}
                    />
                  </Form.Group>

                  <ReCAPTCHA ref={recaptchaRef} sitekey={recaptchaKey} size="invisible"/>
                  <Button
                    className="mt-4 btn-secondary text-center w-100"
                    disabled={disableSave()}
                    name="submit-request"
                    onClick={submitForm}
                  >
                    {
                      saved ? fetchTranslation('request_sent')
                        : fetchTranslation('contact_broker')
                    }
                  </Button>
                </React.Fragment>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <div className="mt-auto"></div>
    </React.Fragment>
  );
}

RequestMoreInfo.propTypes = {
  accountPath: PropTypes.string.isRequired,
  propertyId: PropTypes.number.isRequired,
  recaptchaKey: PropTypes.string.isRequired,
  setShowInfoRequest: PropTypes.func.isRequired,
  sharingId: PropTypes.string.isRequired
};

export const mapStateToProps = ({ vault }) => ({
  propertyId: vault.propertyId,
  sharingId: vault.sharingId,
  accountPath: vault.profileUrl
});
export default connect(mapStateToProps, { setShowInfoRequest })(RequestMoreInfo);
